import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AppShell, { PageContainer } from "@/components/AppShell";
import { api } from "@/lib/api";
import { supabase } from "@/lib/supabaseClient";
import { useSupabaseSession } from "@/lib/SupabaseSessionContext";
import { usePresence } from "@/lib/PresenceContext";
import PresenceDot from "@/components/PresenceDot";
import Avatar from "@/components/Avatar";
import { useT } from "@/lib/i18n";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, MessageSquare, MapPin, Languages as LangIcon, Mic, MicOff, Crown, Gamepad2, UserPlus, UserCheck, Clock, Swords, X } from "lucide-react";
import { toast } from "sonner";

export default function PublicProfilePage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { ready, supabaseUserId } = useSupabaseSession();
  const { isOnline } = usePresence();
  const { lang } = useT();
  const isDE = (lang || "de").startsWith("de");
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [friendStatus, setFriendStatus] = useState(null); // 'none' | 'pending_out' | 'pending_in' | 'accepted'
  const [friendRowId, setFriendRowId] = useState(null);
  const [friendCount, setFriendCount] = useState(0);
  const [squadPickerOpen, setSquadPickerOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/u/${encodeURIComponent(username)}`)
      .then((r) => setProfile(r.data))
      .catch((e) => { if (e?.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));

    // SEO meta
    const orig = document.title;
    document.title = `${username} · FicoPilot`;
    return () => { document.title = orig; };
  }, [username]);

  // Friendship status + count (RLS lets each user see their own friendship rows)
  useEffect(() => {
    if (!ready || !supabase || !profile?.user_id || !supabaseUserId) return;
    if (profile.user_id === supabaseUserId) return;
    (async () => {
      try {
        const { data } = await supabase
          .from("friendships")
          .select("id, requester_id, addressee_id, status")
          .or(`and(requester_id.eq.${supabaseUserId},addressee_id.eq.${profile.user_id}),and(requester_id.eq.${profile.user_id},addressee_id.eq.${supabaseUserId})`)
          .limit(1)
          .maybeSingle();
        if (!data) { setFriendStatus("none"); setFriendRowId(null); return; }
        setFriendRowId(data.id);
        if (data.status === "accepted") setFriendStatus("accepted");
        else if (data.requester_id === supabaseUserId) setFriendStatus("pending_out");
        else setFriendStatus("pending_in");
      } catch (_e) { setFriendStatus("none"); }
    })();
    (async () => {
      try {
        const { count } = await supabase
          .from("friendships")
          .select("id", { count: "exact", head: true })
          .eq("status", "accepted")
          .or(`requester_id.eq.${profile.user_id},addressee_id.eq.${profile.user_id}`);
        setFriendCount(count || 0);
      } catch (_e) { /* noop */ }
    })();
  }, [ready, supabase, profile?.user_id, supabaseUserId]);

  const sendFriendRequest = async () => {
    if (!profile?.user_id || !supabaseUserId) return;
    setFriendStatus("pending_out");
    try {
      const { error } = await supabase.from("friendships").insert({
        requester_id: supabaseUserId,
        addressee_id: profile.user_id,
        status: "pending",
      });
      if (error) throw error;
      toast.success(isDE ? "Anfrage gesendet" : "Request sent");
      api.post("/friends/notify", {
        addressee_user_id: profile.user_id, kind: "friend_request",
      }).catch(() => {});
    } catch (_e) {
      setFriendStatus("none");
      toast.error(isDE ? "Anfrage fehlgeschlagen" : "Couldn't send request");
    }
  };

  const acceptFriendRequest = async () => {
    if (!friendRowId) return;
    setFriendStatus("accepted");
    try {
      await supabase.from("friendships").update({ status: "accepted" }).eq("id", friendRowId);
      toast.success(isDE ? "Freund hinzugefügt" : "Friend added");
      api.post("/friends/notify", {
        addressee_user_id: profile.user_id, kind: "request_accepted",
      }).catch(() => {});
    } catch (_e) { setFriendStatus("pending_in"); }
  };

  const startChat = async () => {
    if (!profile?.user_id) return;
    if (profile.user_id === supabaseUserId) {
      toast.message(isDE ? "Das bist du 😄" : "That's you 😄");
      return;
    }
    try {
      // Backend resolves via service-role so this works regardless of the
      // Supabase JWT bridge state — no more "Sitzung wird vorbereitet" race.
      const { data } = await api.post("/dm/open", { target_user_id: profile.user_id });
      const convId = data?.conversation_id;
      if (!convId) throw new Error("no_conversation_id");
      navigate(`/app/chat/${convId}`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[dm] open failed", e?.response?.status, e?.response?.data);
      const code = e?.response?.data?.detail?.error || e?.response?.data?.detail;
      const msg = code === "cannot_dm_self"
        ? (isDE ? "Du kannst dir nicht selbst schreiben" : "Can't DM yourself")
        : (isDE ? "Chat konnte nicht geöffnet werden" : "Couldn't open chat");
      toast.error(msg);
    }
  };

  if (notFound) {
    return (
      <AppShell>
        <PageContainer>
          <Link to="/app/teammates" className="text-sm text-zinc-400 hover:text-neon inline-flex items-center gap-1 mb-4">
            <ArrowLeft size={14} /> {isDE ? "Zurück" : "Back"}
          </Link>
          <div className="rounded-3xl border border-white/10 bg-zinc-900/50 p-10 text-center">
            <h2 className="font-display text-2xl font-bold">{isDE ? "Profil nicht gefunden" : "Profile not found"}</h2>
          </div>
        </PageContainer>
      </AppShell>
    );
  }

  if (loading || !profile) {
    return (
      <AppShell>
        <PageContainer>
          <div className="rounded-3xl border border-white/5 bg-zinc-900/50 h-64 animate-pulse" />
        </PageContainer>
      </AppShell>
    );
  }

  const avatarSrc = profile.avatar_url;

  return (
    <AppShell>
      <PageContainer className="!max-w-3xl">
        <Link to="/app/teammates" className="text-sm text-zinc-400 hover:text-neon inline-flex items-center gap-1 mb-4">
          <ArrowLeft size={14} /> {isDE ? "Zurück" : "Back"}
        </Link>

        <div className="rounded-3xl overflow-hidden border border-white/5 bg-gradient-to-b from-zinc-900/80 to-zinc-950 mb-5">
          {profile.banner_url ? (
            <div className="h-32 sm:h-44 bg-zinc-900 overflow-hidden relative">
              <img src={profile.banner_url} alt="" className="w-full h-full object-cover" data-testid="pp-banner" />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/80 to-transparent" />
            </div>
          ) : (
            <div className="h-28 bg-gradient-to-r from-fuchsia-500/20 via-cyan-500/20 to-emerald-500/20" />
          )}
          <div className="px-6 pb-6 -mt-12 sm:flex sm:items-end sm:justify-between sm:gap-6">
            <div className="flex items-end gap-4">
              <div className="relative">
                <Avatar src={avatarSrc} name={profile.username} size={96} className="border-4 border-zinc-950" testId="pp-avatar" />
                {(isOnline(profile.user_id) || profile.is_online) && (
                  <PresenceDot userId={profile.user_id} size={16} className="absolute bottom-1 right-1" testId="pp-online" />
                )}
              </div>
              <div className="pb-1">
                <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tight" data-testid="pp-username">
                  {profile.username}
                </h1>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  {profile.is_premium && (
                    <Badge className="bg-amber-500/15 border-amber-500/30 text-amber-300 inline-flex items-center gap-1">
                      <Crown size={11} /> Premium
                    </Badge>
                  )}
                  {profile.mode && (
                    <Badge className="bg-cyan-500/15 border-cyan-500/30 text-cyan-300 capitalize">{profile.mode}</Badge>
                  )}
                  {profile.mic ? (
                    <Badge className="bg-emerald-500/15 border-emerald-500/30 text-emerald-300 inline-flex items-center gap-1"><Mic size={11} /> Mic</Badge>
                  ) : (
                    <Badge className="bg-zinc-500/15 border-zinc-500/30 text-zinc-400 inline-flex items-center gap-1"><MicOff size={11} /> No mic</Badge>
                  )}
                  {friendCount > 0 && (
                    <Badge className="bg-white/5 border-white/10 text-zinc-300" data-testid="pp-friend-count">
                      {friendCount} {isDE ? "Freunde" : "friends"}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-4 sm:mt-0 flex gap-2 flex-wrap">
              {profile.user_id !== supabaseUserId && (
                <>
                  <Button onClick={startChat} className="rounded-full bg-neon text-black hover:bg-neon-hover font-bold" data-testid="pp-chat-cta">
                    <MessageSquare size={14} className="mr-2" /> {isDE ? "Nachricht senden" : "Send message"}
                  </Button>
                  {friendStatus === "none" && (
                    <Button onClick={sendFriendRequest} variant="outline" className="rounded-full bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-white" data-testid="pp-add-friend">
                      <UserPlus size={14} className="mr-2" /> {isDE ? "Freund hinzufügen" : "Add friend"}
                    </Button>
                  )}
                  {friendStatus === "pending_out" && (
                    <Button disabled variant="outline" className="rounded-full bg-transparent border-white/10 text-zinc-400" data-testid="pp-friend-pending">
                      <Clock size={14} className="mr-2" /> {isDE ? "Anfrage gesendet" : "Request sent"}
                    </Button>
                  )}
                  {friendStatus === "pending_in" && (
                    <Button onClick={acceptFriendRequest} className="rounded-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold" data-testid="pp-friend-accept">
                      <UserCheck size={14} className="mr-2" /> {isDE ? "Annehmen" : "Accept"}
                    </Button>
                  )}
                  {friendStatus === "accepted" && (
                    <Button disabled variant="outline" className="rounded-full bg-transparent border-emerald-500/30 text-emerald-300" data-testid="pp-friend-accepted">
                      <UserCheck size={14} className="mr-2" /> {isDE ? "Befreundet" : "Friends"}
                    </Button>
                  )}
                  {(friendStatus === "accepted") && (
                    <Button
                      onClick={() => setSquadPickerOpen(true)}
                      variant="outline"
                      className="rounded-full bg-transparent border-fuchsia-500/30 text-fuchsia-300 hover:bg-fuchsia-500/10 hover:text-fuchsia-200"
                      data-testid="pp-invite-squad"
                    >
                      <Swords size={14} className="mr-2" /> {isDE ? "Zum Squad einladen" : "Invite to squad"}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {profile.bio && (
          <div className="rounded-2xl border border-white/5 bg-zinc-900/50 p-5 mb-5" data-testid="pp-bio">
            <h3 className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono mb-2">{isDE ? "Über mich" : "About"}</h3>
            <p className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed">{profile.bio}</p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={isDE ? "Land" : "Country"} value={profile.country} Icon={MapPin} testid="pp-country" />
          <Field label={isDE ? "Sprachen" : "Languages"} value={(profile.languages || []).join(", ")} Icon={LangIcon} testid="pp-languages" />
          <Field label={isDE ? "Lieblingsspiele" : "Favorite games"} value={(profile.favorite_games || []).join(", ")} Icon={Gamepad2} testid="pp-games" className="sm:col-span-2" />
          {profile.playstyle && <Field label={isDE ? "Playstyle" : "Playstyle"} value={profile.playstyle} testid="pp-playstyle" />}
          {(profile.recently_played || []).length > 0 && (
            <Field label={isDE ? "Zuletzt gespielt" : "Recently played"} value={profile.recently_played.join(", ")} testid="pp-recent" className="sm:col-span-2" />
          )}
        </div>

        {squadPickerOpen && (
          <SquadPickerModal
            isDE={isDE}
            targetUserId={profile.user_id}
            targetUsername={profile.username}
            myId={supabaseUserId}
            onClose={() => setSquadPickerOpen(false)}
          />
        )}
      </PageContainer>
    </AppShell>
  );
}

function Field({ label, value, Icon, className = "", testid }) {
  if (!value) return null;
  return (
    <div className={`rounded-2xl border border-white/5 bg-zinc-900/50 p-4 ${className}`} data-testid={testid}>
      <h3 className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono mb-1 inline-flex items-center gap-1.5">
        {Icon && <Icon size={11} />} {label}
      </h3>
      <p className="text-sm text-zinc-100">{value}</p>
    </div>
  );
}

function SquadPickerModal({ isDE, targetUserId, targetUsername, myId, onClose }) {
  const [squads, setSquads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sentSet, setSentSet] = useState(new Set());
  const [myName, setMyName] = useState("");

  useEffect(() => {
    (async () => {
      try {
        // Find squads where I'm a member
        const { data: mems } = await supabase
          .from("squad_members")
          .select("squad_id")
          .eq("user_id", myId);
        const ids = (mems || []).map((m) => m.squad_id);
        if (ids.length === 0) { setSquads([]); return; }
        const { data: rows } = await supabase
          .from("squads")
          .select("id, name, owner_id, game, mode, max_size")
          .in("id", ids);
        const { data: counts } = await supabase
          .from("squad_members")
          .select("squad_id, user_id")
          .in("squad_id", ids);
        const cMap = {}; (counts || []).forEach((c) => { cMap[c.squad_id] = (cMap[c.squad_id] || 0) + 1; });
        const memberOfSet = new Set((counts || []).filter((c) => c.user_id === targetUserId).map((c) => c.squad_id));

        // Check existing pending invites for target
        const { data: invs } = await supabase
          .from("squad_invites")
          .select("squad_id")
          .eq("invitee_id", targetUserId)
          .eq("status", "pending")
          .in("squad_id", ids);
        const invitedSet = new Set((invs || []).map((i) => i.squad_id));

        setSquads((rows || []).map((s) => ({
          ...s,
          _count: cMap[s.id] || 0,
          _alreadyMember: memberOfSet.has(s.id),
          _invited: invitedSet.has(s.id),
        })));

        const { data: mine } = await supabase.from("profiles").select("username").eq("user_id", myId).maybeSingle();
        setMyName(mine?.username || "");
      } catch (_e) { setSquads([]); }
      finally { setLoading(false); }
    })();
  }, [myId, targetUserId]);

  const send = async (s) => {
    setSentSet((set) => new Set(set).add(s.id));
    try {
      const { error } = await supabase.from("squad_invites").insert({
        inviter_id: myId,
        invitee_id: targetUserId,
        squad_id: s.id,
        game: s.game,
        mode: s.mode,
        status: "pending",
      });
      if (error) throw error;
      toast.success(isDE ? "Einladung gesendet" : "Invite sent");
      api.post("/squads/notify", {
        invitee_user_id: targetUserId,
        kind: "squad_invite",
        inviter_username: myName,
        squad_name: s.name,
      }).catch(() => {});
    } catch (_e) {
      setSentSet((set) => { const n = new Set(set); n.delete(s.id); return n; });
      toast.error(isDE ? "Konnte nicht senden" : "Couldn't send");
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/70 backdrop-blur" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-950 p-5" onClick={(e) => e.stopPropagation()} data-testid="pp-squad-picker">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-base inline-flex items-center gap-2">
            <Swords size={14} className="text-neon" />
            {isDE ? `${targetUsername} einladen zu…` : `Invite ${targetUsername} to…`}
          </h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-rose-400"><X size={16} /></button>
        </div>

        <div className="max-h-80 overflow-y-auto -mx-1">
          {loading ? (
            <p className="text-xs text-zinc-500 px-3 py-6 text-center">{isDE ? "Lade Squads…" : "Loading squads…"}</p>
          ) : squads.length === 0 ? (
            <div className="px-3 py-6 text-center">
              <p className="text-xs text-zinc-500 mb-2">{isDE ? "Du bist in keinem Squad." : "You're not in any squad."}</p>
              <Link to="/app/squads" onClick={onClose} className="text-xs text-neon hover:underline" data-testid="pp-create-squad-link">
                {isDE ? "Squad erstellen →" : "Create one →"}
              </Link>
            </div>
          ) : (
            <ul className="space-y-1">
              {squads.map((s) => {
                const full = s._count >= s.max_size;
                const sent = sentSet.has(s.id) || s._invited;
                const disabled = sent || full || s._alreadyMember;
                return (
                  <li key={s.id} className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/5" data-testid={`pp-squad-row-${s.id}`}>
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-neon to-blue-500 grid place-items-center text-black font-display font-black text-xs shrink-0">
                      {(s.name || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{s.name}</p>
                      <p className="text-[10px] text-zinc-500">
                        {s._count}/{s.max_size} · {s.game || (isDE ? "beliebig" : "any")}
                      </p>
                    </div>
                    <Button
                      size="sm" onClick={() => send(s)} disabled={disabled}
                      className={`rounded-full font-bold h-7 text-[11px] ${
                        disabled ? "bg-zinc-800 text-zinc-500" : "bg-neon text-black hover:bg-neon-hover"
                      }`}
                      data-testid={`pp-squad-send-${s.id}`}
                    >
                      {s._alreadyMember
                        ? (isDE ? "Mitglied" : "Member")
                        : sent
                          ? (isDE ? "Gesendet" : "Sent")
                          : full
                            ? (isDE ? "Voll" : "Full")
                            : (isDE ? "Einladen" : "Invite")}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
